import React, { ReactElement, useMemo } from 'react'
import { Box, Card, Link, Typography } from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'

import {
  AccreditationHealthRow,
  AccreditationPendingStatus,
  ACCREDITATION_COLUMNS,
} from 'src/v3/content-pipeline/accreditation-health.slice'
import { PENDING_META } from 'src/v3/content-pipeline/accreditation/utils/accreditation-status-meta'

let PENDING_COLUMNS: AccreditationPendingStatus[] = [
  'draft',
  'submitted',
  'approved',
  'rejected',
]

type Props = {
  rows: AccreditationHealthRow[]
  onSelectRow: (id: string) => void
}

export default function AccreditationDraftPipelineTab({
  rows,
  onSelectRow,
}: Props): ReactElement {
  let cards = useMemo(
    () =>
      rows.flatMap((row) =>
        ACCREDITATION_COLUMNS.filter(
          (col) => row.accreditations[col.key].pending,
        ).map((col) => ({
          rowId: row.id,
          rowLabel: row.label,
          accreditationLabel: col.label,
          pending: row.accreditations[col.key]
            .pending as AccreditationPendingStatus,
          isNotPursued: row.accreditations[col.key].status === 'notPursued',
        })),
      ),
    [rows],
  )

  let cardsByColumn = PENDING_COLUMNS.map((pending) => ({
    pending,
    cards: cards.filter((c) => c.pending === pending),
  }))

  if (cards.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'background.paperDark',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          No Updates In Review
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Every accreditation update has been finalized — nothing is currently
          mid-review.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
        {cards.length} accreditation update{cards.length === 1 ? '' : 's'}{' '}
        {cards.length === 1 ? 'is' : 'are'} mid-review. Each card is independent
        of the accreditation's current live status.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {cardsByColumn.map(({ pending, cards: columnCards }) => {
          let meta = PENDING_META[pending]
          return (
            <Box key={pending}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mb: 1,
                }}
              >
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 12,
                    color:
                      meta.color === 'default'
                        ? 'text.disabled'
                        : `${meta.color}.main`,
                  }}
                />
                <Typography sx={{ fontWeight: 600 }}>{meta.label}</Typography>
                <Typography color="text.secondary">
                  {columnCards.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {columnCards.map((card) => (
                  <Card
                    key={`${card.rowId}-${card.accreditationLabel}`}
                    variant="outlined"
                    sx={{ p: 1.5 }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      {card.rowLabel}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.accreditationLabel} accreditation
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        mt: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {card.isNotPursued
                          ? `Not pursued · ${meta.description.toLowerCase()}`
                          : meta.description}
                      </Typography>
                      <Link
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => onSelectRow(card.rowId)}
                        aria-label={`Review ${card.accreditationLabel} update for ${card.rowLabel}`}
                      >
                        Review
                      </Link>
                    </Box>
                  </Card>
                ))}
                {columnCards.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Nothing here.
                  </Typography>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
